import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useEnforcement } from '@/hooks/useEnforcement';
import { isFreeAutomationGateActive } from '@/lib/freeAutomationGate';
import { toast } from 'sonner';
import { Save, Sparkles, ChevronDown, Lock } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface CustomInstructionsProps {
  feedId: string;
  initialValue: string | null;
  embedded?: boolean;
}

const CustomInstructions: React.FC<CustomInstructionsProps> = ({ feedId, initialValue, embedded }) => {
  const { t } = useTranslation('products');
  const { plan, planKnown, isLoading: enforcementLoading } = useEnforcement();
  const [instructions, setInstructions] = useState(initialValue || '');
  const [savedValue, setSavedValue] = useState(initialValue || '');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setInstructions(initialValue || '');
    setSavedValue(initialValue || '');
  }, [initialValue, feedId]);

  const hasChanges = instructions !== savedValue;
  const hasInstructions = savedValue.trim().length > 0;

  // Preisumbau: Custom Prompts sind Teil der bezahlten Baender — fuer Free
  // gesperrt AB DEM STICHTAG, synchron zum Automatik-Gate
  // (isFreeAutomationGateActive, VITE_FREE_AUTOMATION_GATE_AT). Das harte Gate
  // sitzt serverseitig: der per Migration 20260821200000 auf denselben
  // Stichtag datierte Trigger revertiert Free-Writes auf
  // feeds.custom_prompt_instructions still; diese Sperre ist die ehrliche
  // Anzeige dazu. `!enforcementLoading` verhindert nur das kurze Aufblitzen
  // einer Sperre bei zahlenden Kunden, solange der Plan noch laedt.
  const locked = !enforcementLoading && planKnown && plan === 'free' && isFreeAutomationGateActive();

  const handleSave = async () => {
    if (locked) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('feeds')
        .update({ custom_prompt_instructions: instructions || null } as any)
        .eq('id', feedId);

      if (error) throw error;

      setSavedValue(instructions);
      toast.success(t('customInstructions.savedSuccess'));
    } catch (err) {
      console.error('Failed to save custom instructions:', err);
      Sentry.captureException(err, { tags: { feature: 'feeds', action: 'save-custom-instructions' } });
      toast.error(t('customInstructions.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <>
      <p className="text-xs text-muted-foreground mb-3">
        {t('customInstructions.description')}
      </p>
      <Textarea
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
        placeholder={t('customInstructions.placeholder')}
        className="min-h-[80px] text-sm"
        rows={3}
        disabled={locked}
      />
      {locked ? (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
          <Lock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          <span>
            {t('customInstructions.freeGateHint')}{' '}
            <Link
              to="/pricing"
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              {t('customInstructions.freeGateCta')}
            </Link>
          </span>
        </p>
      ) : (
        <div className="flex justify-end mt-2">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {saving ? t('customInstructions.saving') : t('customInstructions.save')}
          </Button>
        </div>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-2 w-full text-left">
              <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
              <span className="text-sm font-medium text-foreground flex-1">{t('customInstructions.title')}</span>
              {hasInstructions && !open && (
                <span className="text-xs text-muted-foreground truncate max-w-[250px]">{savedValue.slice(0, 50)}…</span>
              )}
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2">
              {content}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};

export default CustomInstructions;
